import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { colors } from "../../../theme/colors";

interface AddNewManagerFormData {
  fullName: string;
  email: string;
  phone: string;
  userPassword: string;
  stationType: string;
}

interface EditManagerInitialData {
  fullName: string;
  email: string;
  phone: string;
  stationType: string;
}

interface AddNewManagerProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AddNewManagerFormData) => Promise<boolean>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
  initialData?: EditManagerInitialData;
  isEditMode?: boolean;
}

interface TouchedFields {
  fullName?: boolean;
  email?: boolean;
  phone?: boolean;
  userPassword?: boolean;
  stationType?: boolean;
}

const AddNewManager: React.FC<AddNewManagerProps> = ({
  open,
  onClose,
  onSave,
  isSubmitting = false,
  errorMessage = null,
  onClearError,
  initialData,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<AddNewManagerFormData>({
    fullName: "",
    email: "",
    phone: "",
    userPassword: "",
    stationType: "",
  });
  const [errors, setErrors] = useState<Partial<AddNewManagerFormData>>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (open) {
      if (initialData) {
        console.log("Setting form data with initial data:", initialData);
        setFormData({
          fullName: initialData.fullName,
          email: initialData.email,
          phone: initialData.phone,
          userPassword: "",
          stationType: initialData.stationType,
        });
      } else {
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          userPassword: "",
          stationType: "",
        });
      }
      setErrors({});
      setTouched({});
      setShowPassword(false);
    }
  }, [open, initialData]);

  const stationTypes = [
    { label: "Filling", value: "Filling" },
    { label: "Testing", value: "Testing" },
  ];

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      backgroundColor: "#FFFFFF",
      minHeight: 54,
      "& fieldset": {
        borderColor: "#D1D5DB",
      },
      "&:hover fieldset": {
        borderColor: "#C4CBD5",
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary,
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "16px",
      color: colors.black,
      fontFamily: "'Inter', sans-serif",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
  };

  const getSelectDisplayValue = (
    value: string,
    options: Array<{ label: string; value: string }>,
    placeholder: string,
  ) => {
    if (!value) {
      return (
        <Box component="span" sx={{ color: "#9CA3AF" }}>
          {placeholder}
        </Box>
      );
    }

    return options.find((option) => option.value === value)?.label || value;
  };

  const FieldLabel: React.FC<{
    label: string;
    required?: boolean;
    optional?: boolean;
  }> = ({ label, required = false, optional = false }) => (
    <Typography
      sx={{
        mb: 1,
        fontSize: "14px",
        fontWeight: 600,
        color: "#1F2937",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {label}
      {required ? " *" : ""}
      {optional ? " (Optional)" : ""}
    </Typography>
  );

  const handleInputChange =
    (field: keyof AddNewManagerFormData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent,
    ) => {
      const value =
        field === "phone"
          ? event.target.value.replace(/\D/g, "").slice(0, 10)
          : event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setTouched((prev) => ({ ...prev, [field]: true }));
      onClearError?.();

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const validateField = (
    field: keyof AddNewManagerFormData,
    value: string,
  ): string => {
    if (!value || value.trim() === "") {
      if (field === "fullName") return "Full Name is required";
      if (field === "email") return "Email Address is required";
      if (field === "phone") return "Phone is required";
      if (field === "userPassword" && !isEditMode)
        return "Password is required";
      if (field === "userPassword" && isEditMode) return "";
      if (field === "stationType") return "Station type is required";
      return "";
    }

    if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Please enter a valid email address";
    }

    if (field === "phone" && value && !/^\d{10}$/.test(value)) {
      return "Phone number must be exactly 10 digits";
    }

    if (field === "userPassword" && value.length < 8) {
      return "Password must be at least 8 characters long";
    }

    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<AddNewManagerFormData> = {};
    let isValid = true;

    const requiredFields: Array<keyof AddNewManagerFormData> = isEditMode
      ? ["fullName", "email", "phone", "stationType"]
      : ["fullName", "email", "phone", "userPassword", "stationType"];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    if (isEditMode && formData.userPassword) {
      const passwordError = validateField(
        "userPassword",
        formData.userPassword,
      );
      if (passwordError) {
        newErrors.userPassword = passwordError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    setTouched({
      fullName: true,
      email: true,
      phone: true,
      userPassword: true,
      stationType: true,
    });

    return isValid;
  };

  const handleSave = async () => {
    if (validateForm()) {
      const isSaved = await onSave(formData);
      if (isSaved) {
        handleReset();
        onClose();
      }
    }
  };

  const handleReset = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      userPassword: "",
      stationType: "",
    });
    setErrors({});
    setTouched({});
    setShowPassword(false);
  };

  const handleClose = () => {
    onClearError?.();
    handleReset();
    onClose();
  };

  const isFieldError = (field: keyof AddNewManagerFormData): boolean => {
    return !!errors[field] && !!touched[field];
  };

  const getFieldError = (field: keyof AddNewManagerFormData): string => {
    return errors[field] || "";
  };

  const hasEditFormChanges = (() => {
    if (!isEditMode || !initialData) {
      return true;
    }

    return (
      formData.fullName.trim() !== initialData.fullName.trim() ||
      formData.email.trim() !== initialData.email.trim() ||
      formData.phone.trim() !== initialData.phone.trim() ||
      formData.stationType !== initialData.stationType ||
      formData.userPassword.trim() !== ""
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            maxWidth: 760,
            boxShadow: "0 28px 60px rgba(15, 23, 42, 0.22)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 5 },
          pt: { xs: 3, sm: 4 },
          pb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              color: colors.black,
              lineHeight: 1.15,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {isEditMode ? "Edit User" : "Add New User"}
          </Typography>
          <Typography
            sx={{
              color: colors.textGray,
              mt: 1,
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {isEditMode
              ? "Update user account details"
              : "Fill in the details to create a manager"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 54,
            height: 54,
            bgcolor: "#F3F4F6",
            color: "#111827",
            "&:hover": {
              bgcolor: "#E5E7EB",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, pt: 1, pb: { xs: 4, sm: 5 } }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12 }}>
            <FieldLabel label="Full Name" required />
            <TextField
              fullWidth
              placeholder="e.g. Aryan Mehta"
              value={formData.fullName}
              onChange={handleInputChange("fullName")}
              onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
              error={isFieldError("fullName")}
              helperText={
                isFieldError("fullName") ? getFieldError("fullName") : ""
              }
              disabled={isSubmitting}
              sx={textFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FieldLabel label="Email Address" required />
            <TextField
              fullWidth
              placeholder="e.g. aryan.mehta@craksha.com"
              value={formData.email}
              onChange={handleInputChange("email")}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              error={isFieldError("email")}
              helperText={isFieldError("email") ? getFieldError("email") : ""}
              disabled={isSubmitting}
              sx={textFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FieldLabel label="Phone" required />
            <TextField
              fullWidth
              placeholder="Enter 10-digit phone number"
              value={formData.phone}
              onChange={handleInputChange("phone")}
              onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
              error={isFieldError("phone")}
              helperText={isFieldError("phone") ? getFieldError("phone") : ""}
              disabled={isSubmitting}
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                },
              }}
              sx={textFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FieldLabel
              label="Password"
              required={!isEditMode}
              optional={isEditMode}
            />
            <TextField
              fullWidth
              autoComplete="off"
              required={!isEditMode}
              placeholder="Enter User Password"
              type={showPassword ? "text" : "password"}
              value={formData.userPassword}
              onChange={handleInputChange("userPassword")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, userPassword: true }))
              }
              error={isFieldError("userPassword")}
              helperText={
                isFieldError("userPassword")
                  ? getFieldError("userPassword")
                  : ""
              }
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={textFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FieldLabel label="Station type" required />
            <TextField
              select
              fullWidth
              value={formData.stationType}
              onChange={handleInputChange("stationType")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, stationType: true }))
              }
              disabled={isSubmitting}
              error={isFieldError("stationType")}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected: unknown) =>
                    getSelectDisplayValue(
                      selected as string,
                      stationTypes,
                      "Select station type",
                    ),
                },
              }}
              sx={textFieldSx}
            >
              {stationTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            {isFieldError("stationType") && (
              <Typography
                variant="caption"
                sx={{ color: "#D32F2F", mt: 0.75, display: "block" }}
              >
                {getFieldError("stationType")}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 1.5 }}>
            <Button
              fullWidth
              onClick={handleSave}
              variant="contained"
              disabled={isSubmitting || (isEditMode && !hasEditFormChanges)}
              sx={{
                bgcolor: colors.primary,
                borderRadius: 1.5,
                minHeight: 56,
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 600,
                boxShadow: "0 4px 10px rgba(47, 125, 50, 0.22)",
                "&:hover": {
                  bgcolor: "#256B29",
                },
              }}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update User"
                  : "Save User"}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export type { AddNewManagerFormData, AddNewManagerProps };
export default AddNewManager;
